import { IRefFoodTags } from '../../models/ref-food-tags/ref-food-tags.interface';
import { RefFoodTagsAction, RefFoodTagsState } from '../../models/ref-food-tags/ref-food-tags.redux';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { IBlockPeriod, IMealPlan, IMealPlanResponse, IPatientPlan, IPlanPeriod, MealPlanActions, MealPlanState } from '../../models/meal-plans';
import { MealPlanClass } from '../../class/meal-plan/meal-plan.class';
import { Loader } from '../../models/loader/loader.redux';
import { BlockPeriodClass } from '../../class/meal-plan/block-period.class';
import { PlanPeriodClass } from '../../class/meal-plan/plan-period.class';
import { Logger } from '../../class/helper/logger.class';
import { firstValueFrom } from 'rxjs';
import { Params, Router } from '@angular/router';
import { IUser } from '../../models/auth';
import { IFoodGroupIngredient } from '../../models/food-group';
import { IIndicatorsPatientConsultation } from '../../models/measurement';
import { PatientAction, PatientState } from '../../models/patient';
import { MealPlanBuilderState } from '../../models/meal-plan-builder';
import { IngredientService } from '../ingredient/ingredient.service';
import { MealTimeClass } from 'src/app/class/meal-plan/meal-time.class';
import { IMealTime } from 'src/app/models/meal-plans/meal-time.interface';

@Injectable({
  providedIn: 'root'
})
export class MealPlansService {
  // tslint:disable:no-redundant-jsdoc
  constructor(
    private store: Store,
    private router: Router,
    private ingredientService: IngredientService
  ) { }

  /**
   * POST Plan alimentación
   * @param clinicId id de la clínica
   * @param mealPlanId identificador del plan de alimentación, o si es nuevo
   * @param mealPlanClass clase meal plan class con los datos para formatear
   * @param patientId el id del paciente
   * @param expertId
   * @param appointmentId el id de la cita
   * @param isCloneMode
   */
  async saveMealPlan(
    clinicId: string,
    mealPlanId: string,
    mealPlanClass: MealPlanClass,
    patientId: string = null,
    expertId: string = null,
    appointmentId: string = null,
    isCloneMode: boolean = false
  ): Promise<void> {
    const mealPlanClassRaw = cloneDeep(mealPlanClass.getRaw());
    try {
      if (mealPlanId !== 'new' && isCloneMode) {
        await firstValueFrom((this.store.dispatch(new MealPlanActions.Create(clinicId, mealPlanClassRaw))));
      } else if (mealPlanId !== 'new' && !isCloneMode) {
        // verificar si es para un plan general o un plan para el paciente
        if (patientId) {
          await firstValueFrom((this.store.dispatch(new MealPlanActions.UpdateMealPlanToPatient(patientId, expertId, mealPlanId, mealPlanClassRaw))));
        } else {
          await firstValueFrom((this.store.dispatch(new MealPlanActions.Update(clinicId, mealPlanId, mealPlanClassRaw))));
        }
      } else {
        if (patientId) {
          await firstValueFrom(
            this.store.dispatch(
              new MealPlanActions.CreateMealPlanToPatient(expertId, patientId, mealPlanClassRaw, appointmentId ?? undefined)
            )
          );
        } else {
          await firstValueFrom((this.store.dispatch(new MealPlanActions.Create(clinicId, mealPlanClassRaw))));
        }
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }


  async toggleShowMealPlan(clinicId: string, patientId: string, expertId: string, mealPlanId: string): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Guardando ...'))));
      await firstValueFrom((this.store.dispatch(new MealPlanActions.ToggleShowMealPlan(clinicId, patientId, expertId, mealPlanId))));
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  async useMealPlanToAppointment(expertId: string, patientId: string, mealPlanId: string, appointmentId: string): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Asignando ...'))));
      await firstValueFrom((this.store.dispatch(new MealPlanActions.MealPlanToAppointment(expertId, patientId, mealPlanId, appointmentId))));
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  async removeMealPlan(expertId: string, patientId: string, mealPlanId: string): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Eliminando ...'))));
      await firstValueFrom((this.store.dispatch(new MealPlanActions.Remove(expertId, patientId, mealPlanId))));
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  async getStructurePlanPeriods(mealPlan: IMealPlanResponse | IMealPlan): Promise<any> {
    // todo se llama al servicio de ingredient
    const canShowAllergicMode: boolean = await firstValueFrom(this.store.selectOnce(MealPlanBuilderState.getCanShowAllergicMode));
    let allergies: string[] = [];
    let dislikes: string[] = [];
    if (canShowAllergicMode) {
      allergies = await this.ingredientService.getAllergiesPatient();
      dislikes = await this.ingredientService.getDislikesIngredientsPatient();
    }

    const planPeriodsClass: PlanPeriodClass[] = [];
    if (mealPlan.planPeriods) {
      mealPlan.planPeriods.forEach((period: IPlanPeriod) => {
        const mealTimes: MealTimeClass[] = [];
        if (period.mealTimes) {
          period.mealTimes.forEach((mealTime: IMealTime) => {
            const blockPeriodsClass: BlockPeriodClass[] = [];
            if (mealTime.blockPeriods) {
              mealTime.blockPeriods.forEach((block: IBlockPeriod) => {
                if (canShowAllergicMode) {
                  block.ingredients.forEach((ingredient: IFoodGroupIngredient) => {
                    ingredient.isDislike = dislikes.includes(ingredient.ingredientId);
                    ingredient.isAllergic = allergies.includes(ingredient.ingredientId);
                  });
                }
                blockPeriodsClass.push(new BlockPeriodClass(block));
              });
            }
            mealTimes.push(new MealTimeClass(mealTime, blockPeriodsClass));
          });
        }
        planPeriodsClass.push(new PlanPeriodClass(period, [], mealTimes, false));
      });
    }
    return planPeriodsClass;
  }

  async getRefFoodCategories(): Promise<IRefFoodTags[]> {
    try {
      const data = cloneDeep(await firstValueFrom((this.store.selectOnce(RefFoodTagsState.getRegFoodTags))));
      if (data && data.length > 0) {
        return data;
      }
      await firstValueFrom((this.store.dispatch(new RefFoodTagsAction.Get())));
      return cloneDeep(await firstValueFrom((this.store.selectOnce(RefFoodTagsState.getRegFoodTags))));
    } catch (e) {
      Logger.log(e);
    }
  }

  /**
   * Esta función es usada para el botón de regresar en las páginas MealPlanBuilderPage y MealPlanFinderPage
   */
  async onBackMealPlanPageOrConsultation(params: Params, queryParams: Params): Promise<void> {
    if (queryParams.appointmentId) {
      await this.router.navigate(
        ['clinic', params.clinicId, 'patients', params.patientId, 'consultation', 'orientation'],
        { queryParams: { ...queryParams, appointmentId: queryParams.appointmentId } },
      );
      return;
    }
    await this.router.navigate(
      ['/clinic', params.clinicId, 'patients', params.patientId, 'meal-plans'],
      { queryParams }
    );

  }

  /**
   * obtiene el plan de alimentación activo del paciente
   * luego establece los datos para crear la estructura necesaria para el editor de planes
   */
  async getLastMealPlanToPatient(
    userLogged: IUser,
    clinicId: string,
    patientId: string,
    expertId: string
  ): Promise<IPatientPlan> {
    try {
      if (userLogged.experts && userLogged.experts[0]) {
        await firstValueFrom(
          this.store.dispatch(new MealPlanActions.GetLastMealPlanOfPatient(clinicId, patientId, userLogged.experts[0]._id))
        );
      } else if (userLogged.patients && userLogged.patients[0]) {
        await firstValueFrom(
          this.store.dispatch(new MealPlanActions.GetLastMealPlanOfPatient(clinicId, patientId, expertId))
        );
      }

      return await firstValueFrom(this.store.selectOnce(MealPlanState.getActiveMealPlanOfPatient));

      /*if (this.patientPlan?.mealPlan) {
        await this.setMealPlan(
          this.convertMealPlanToDTO(this.patientPlan.mealPlan),
          !!userLogged.patients
        );
      }*/
    } catch (e) {
      console.error('[getLastMealPlanToPatient]', e);
      return null;
    }
  }

  convertMealPlanToDTO(mealPlan: IMealPlan): IMealPlanResponse {
    return {
      name: mealPlan.name,
      planPeriods: mealPlan.planPeriods,
      _id: mealPlan._id,
      nutritionalProperties: mealPlan.nutritionalProperties,
      instructions: mealPlan.instructions,
      hiddenOnList: mealPlan.hiddenOnList,
      expertId: mealPlan.expertId,
      expert: null,
      tags: mealPlan.tags as any[],
      metadata: mealPlan.metadata
    };
  }

}
